import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { CheckCircle, XCircle, Loader2, ArrowLeft, Crown } from 'lucide-react';

export default function SubscriptionSuccessPage() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const { user } = useAuth();
  
  const [status, setStatus] = useState('checking');
  const [transaction, setTransaction] = useState(null);
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      return;
    }

    const pollStatus = async () => {
      try {
        const response = await api.get(`/subscriptions/status/${sessionId}`);
        setTransaction(response.data);
        
        if (response.data.payment_status === 'paid') {
          setStatus('success');
        } else if (response.data.payment_status === 'expired' || response.data.payment_status === 'failed') {
          setStatus('failed');
        } else if (attempts < 5) {
          setTimeout(() => setAttempts(prev => prev + 1), 2000);
        } else {
          setStatus('pending');
        }
      } catch (error) {
        console.error('Error checking status:', error);
        if (attempts < 5) {
          setTimeout(() => setAttempts(prev => prev + 1), 2000);
        } else {
          setStatus('error');
        }
      }
    };

    pollStatus();
  }, [sessionId, attempts]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <div className="glass-card p-8 max-w-md w-full text-center">
        {status === 'checking' && (
          <>
            <Loader2 className="w-16 h-16 mx-auto mb-4 text-primary animate-spin" />
            <h1 className="font-heading text-2xl font-bold mb-2">Processing Subscription</h1>
            <p className="text-muted-foreground">Please wait while we confirm your subscription...</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-r from-primary to-accent flex items-center justify-center">
              <Crown className="w-8 h-8 text-white" />
            </div>
            <h1 className="font-heading text-2xl font-bold mb-2">You're Subscribed!</h1>
            <p className="text-muted-foreground mb-4">
              Welcome to the exclusive community! Your subscription is now active.
            </p>
            <div className="bg-muted/50 p-4 rounded-xl mb-6">
              <p className="text-sm text-muted-foreground">Subscription Amount</p>
              <p className="text-2xl font-bold">${transaction?.amount?.toFixed(2)}/month</p>
            </div>
            <Link to="/home">
              <Button className="rounded-full">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Feed
              </Button>
            </Link>
          </>
        )}

        {status === 'failed' && (
          <>
            <XCircle className="w-16 h-16 mx-auto mb-4 text-destructive" />
            <h1 className="font-heading text-2xl font-bold mb-2">Subscription Failed</h1>
            <p className="text-muted-foreground mb-4">
              Unfortunately, your subscription could not be processed.
            </p>
            <Link to="/home">
              <Button variant="outline" className="rounded-full">
                Back to Feed
              </Button>
            </Link>
          </>
        )}

        {status === 'pending' && (
          <>
            <Loader2 className="w-16 h-16 mx-auto mb-4 text-yellow-500" />
            <h1 className="font-heading text-2xl font-bold mb-2">Payment Pending</h1>
            <p className="text-muted-foreground mb-4">
              Your subscription is being processed. You'll receive a notification once it's active.
            </p>
            <Link to="/home">
              <Button variant="outline" className="rounded-full">
                Back to Feed
              </Button>
            </Link>
          </>
        )}

        {status === 'error' && (
          <>
            <XCircle className="w-16 h-16 mx-auto mb-4 text-destructive" />
            <h1 className="font-heading text-2xl font-bold mb-2">Something Went Wrong</h1>
            <p className="text-muted-foreground mb-4">
              We couldn't verify your subscription status.
            </p>
            <Link to="/home">
              <Button variant="outline" className="rounded-full">
                Back to Feed
              </Button>
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
